import { ALL_FACILITIES_QUERY, type FacilityNode } from '@/modules/companies'
import { useNodes } from '@/utils/hooks/useNodes'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

interface CommonProps extends ComboboxCommonProps<string> {
  alwaysInclude?: Omit<FacilityNode, '__typename'> | null
  exclude?: string[]
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  clearable?: boolean
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  clearable?: false
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function FacilityCombobox({
  mode,
  value,

  alwaysInclude = null,
  exclude = [],
  clearable = false,

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.facility.placeholder',
      search: 'common.search',
      noResults: 'common.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.facility.placeholder',
      search: 'common.search',
      noResults: 'common.no-results',
    },
  })

  const { data: facilitiesData } = useQuery(ALL_FACILITIES_QUERY)

  const facilities = useNodes(facilitiesData?.allFacilities)

  const facilityItems: ComboboxItem[] = (() => {
    const items = facilities
      .filter(
        facility =>
          facility.id !== alwaysInclude?.id && !exclude.includes(facility.id)
      )
      .map<ComboboxItem>(facility => ({
        value: facility.id,
        label: facility.name,
      }))

    if (!!alwaysInclude)
      items.unshift({
        value: alwaysInclude.id,
        label: alwaysInclude.name,
      })

    return items
  })()

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={facilityItems}
        clearable={clearable}
        placeholder={placeholder ?? translations.single.placeholder}
        searchPlaceholder={searchPlaceholder ?? translations.single.search}
        noResultsText={noResultsText ?? translations.single.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={facilityItems}
      placeholder={placeholder ?? translations.multiple.placeholder}
      searchPlaceholder={searchPlaceholder ?? translations.multiple.search}
      noResultsText={noResultsText ?? translations.multiple.noResults}
      onChange={onChange}
    />
  )
}
